import { PhosphorXButton } from "components/Atoms/PhosphorIconButton";
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle } from "react";
import { useIsOverflow } from "utilities/hookutils";
import { PreviewPanelContext } from "./PreviewPanelContext";
import classNames from "classnames";
import { ButtonText } from "components/Atoms/Button";

interface PreviewPanelHeaderProps {
  title: string
}

export const PreviewPanelHeader = (props: React.PropsWithChildren<PreviewPanelHeaderProps>) => {
  const { title, children } = props;
  const { closePanel } = useContext(PreviewPanelContext);

  return <>
    <div className="preview-panel-header">
      <div className="nav-text-regular">{title}</div>
      <div className="grow1">
        {children}
      </div>
      <div className="align-start">
        <PhosphorXButton
          onClick={() => { closePanel(); }}
          layout="in-circle"
        />
      </div>
    </div>

    <div className="preview-panel-divider" />
  </>;
};

export const PreviewPanelFooter = (props: React.PropsWithChildren<unknown>) => {
  const { children } = props;
  const { hasBack, back } = useContext(PreviewPanelContext);

  return <div className="preview-panel-footer">
    {hasBack && <div style={{ marginRight: "auto" }}>
      <ButtonText
        label={RESX.Buttons.btnBack}
        onClick={() => back()}
      />
    </div>}
    {children}
  </div >;
};

interface PreviewPanelContentProps {
  className?: string
}

export const PreviewPanelContent = (props: React.PropsWithChildren<PreviewPanelContentProps>) => {
  const { children, className } = props;
  const { panel } = useContext(PreviewPanelContext);
  const ref = React.createRef<HTMLDivElement>();
  const isOverflow = useIsOverflow(ref, panel?.id);
  return <div
    style={{ paddingRight: isOverflow ? `${12}px` : undefined }}
    ref={ref}
    className={classNames("preview-panel-content", className)}
  >
    {children}
  </div>;
};

export const PreviewPanelHorizontalDivider = () => <div className="preview-panel-divider" />;

export const PreviewPanelVerticalDivider = () => <div className="preview-panel-vertical-divider" />;

type PreviewPanelSize = "small" | "medium" | "large";

interface PreviewPanelProps {
  size: PreviewPanelSize
  trigger: JSX.Element
  id: string
  onOpen?: () => void
}

const getWidthFromSize = (size: PreviewPanelSize) => {
  switch (size) {
    case "small":
      return 300;
    case "medium":
      return 450;
    case "large":
      return 600;
  }
};

export interface PreviewPanelRef {
  open: () => void
}

export const PreviewPanel = forwardRef((props: React.PropsWithChildren<PreviewPanelProps>, ref) => {
  const { trigger, children, size, id, onOpen } = props;
  const width = getWidthFromSize(size);
  const { panel, updatePanel, openPanel } = useContext(PreviewPanelContext);

  const isOpen = panel?.id === id;

  useImperativeHandle(ref, () => ({
    open: onTriggerClick
  }));

  useEffect(() => {
    if (isOpen) {
      updatePanel(<div style={{ minWidth: `${width}px` }} className="preview-panel" key={id}>
        {children}
      </div>);
    }
  }, [children, isOpen, id]);

  const onTriggerClick = () => {
    if (isOpen) {
      return;
    }

    openPanel({
      jsx: <div style={{ minWidth: `${width}px` }} className="preview-panel" key={id}>
        {children}
      </div>,
      id,
      width
    });
    if (onOpen) {
      onOpen();
    }
  };

  return <span className={isOpen ? "preview-panel-open" : undefined} style={{ display: "contents" }} role="button" onClick={onTriggerClick}>
    {trigger}
  </span>;
});

export const useOpenPreviewPanel = () => {
  const { openPanel } = useContext(PreviewPanelContext);

  const openPreviewPanel = useCallback((panel: React.ReactNode, id: string, size: PreviewPanelSize = "medium") => {
    const width = getWidthFromSize(size);
    openPanel({
      jsx: <div style={{ minWidth: `${width}px` }} className="preview-panel" key={id}>
        {panel}
      </div>,
      id,
      width
    });
  }, [openPanel]);

  return openPreviewPanel;
}
